import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserMinus, EyeOff, ShieldOff, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AvatarDisplay from "./AvatarDisplay";

/**
 * BlockMuteManager — lists everyone the signed-in user has blocked or muted
 * with one-click unblock/unmute. Blocks are stricter (severs follows on both
 * sides + suppresses DMs), mutes just soft-hide content.
 *
 * The list refreshes after each action — so the count + rows update live.
 */
export default function BlockMuteManager({ defaultOpen = false }) {
  const [blocked, setBlocked] = useState([]);
  const [muted, setMuted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(!!defaultOpen);

  const refresh = async () => {
    setLoading(true);
    try {
      const [b, m] = await Promise.all([
        api.get("/me/blocked").then((r) => r.data?.blocked || []),
        api.get("/me/muted").then((r) => r.data?.muted || []),
      ]);
      setBlocked(b);
      setMuted(m);
    } catch (e) {
      setBlocked([]);
      setMuted([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const unblock = async (uid) => {
    try {
      await api.delete(`/users/${uid}/block`);
      toast.success("Unblocked");
      setBlocked((prev) => prev.filter((r) => r.user_id !== uid));
    } catch {
      toast.error("Couldn't unblock");
    }
  };
  const unmute = async (uid) => {
    try {
      // Existing /me/mute/{id} endpoint is a toggle — POSTing while the mute
      // already exists removes it. Keeps the API surface minimal.
      await api.post(`/me/mute/${uid}`);
      toast.success("Unmuted");
      setMuted((prev) => prev.filter((r) => r.user_id !== uid));
    } catch {
      toast.error("Couldn't unmute");
    }
  };

  return (
    <div data-testid="block-mute-manager">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="block-mute-toggle"
        className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors"
      >
        <ShieldOff size={11} />
        {open ? "Hide block / mute list" : "Manage blocks & mutes"}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <Section
            title={`BLOCKED · ${blocked.length}`}
            icon={ShieldOff}
            color="#ef4444"
            loading={loading}
            rows={blocked}
            actionLabel="Unblock"
            actionIcon={UserMinus}
            onAction={unblock}
            emptyMsg="// Nobody blocked. Vault stays open."
          />
          <Section
            title={`MUTED · ${muted.length}`}
            icon={EyeOff}
            color="#a78bfa"
            loading={loading}
            rows={muted}
            actionLabel="Unmute"
            actionIcon={X}
            onAction={unmute}
            emptyMsg="// No mutes. All signals live."
          />
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, color, loading, rows, actionLabel, actionIcon: ActionIcon, onAction, emptyMsg }) {
  return (
    <div
      className="border px-4 py-3"
      style={{
        borderColor: `${color}55`,
        background: `radial-gradient(circle at 18% 20%, ${color}0d, transparent 65%), #050505`,
      }}
    >
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase flex items-center gap-1.5 mb-2" style={{ color }}>
        <Icon size={11} /> // {title}
      </div>
      {loading ? (
        <p className="text-zinc-600 font-mono text-[9px] tracking-[0.2em] uppercase">LOADING…</p>
      ) : rows.length === 0 ? (
        <p className="text-zinc-600 font-mono text-[9px] tracking-[0.2em] uppercase">{emptyMsg}</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.user_id} className="flex items-center gap-3 py-1">
              <div className="w-8 h-8 border border-white/10 flex items-center justify-center bg-[#050505] shrink-0">
                <AvatarDisplay avatarId={r.avatar_id} username={r.username} size={24} />
              </div>
              <Link
                to={`/u/${r.username}`}
                className="flex-1 font-mono text-xs tracking-[0.15em] text-zinc-300 hover:text-white truncate"
                data-testid={`bm-row-${r.username}`}
              >
                @{r.username}
              </Link>
              <button
                type="button"
                onClick={() => onAction(r.user_id)}
                data-testid={`bm-act-${r.username}`}
                className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 font-mono text-[9px] tracking-[0.2em] uppercase transition-colors"
              >
                <ActionIcon size={9} /> {actionLabel}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
